import { ColumnDef, createColumnHelper } from '@tanstack/react-table'
import { FbLink } from 'components/FbUI/FbLink'
import { ActivityV2 } from 'models/activityV2'
import CellEventFormatterV2 from './CellEvent/CellEventFormatterV2'
import { MdPersonOutline } from 'react-icons/md'
import { formatInteger } from 'utils/formatting'
import { contentTypeMap } from './utils'
import { createTableColumnHelper } from 'components/DataExpansionComponents/Common/commonColumnHelpers'
import { ACTIVITY_V2_COLUMNS } from 'components/DataExpansionComponents/Activity/activityV2ColumnInfo'
import { OverflownTextTooltip } from 'components/OverflownTextTooltip'
import { Fragment } from 'react'

export const useActivityTableColumnsV2 = (): ColumnDef<ActivityV2, any>[] => {
  const columnHelper = createColumnHelper<ActivityV2>()

  const activityColumnHelper = createTableColumnHelper({
    columns: ACTIVITY_V2_COLUMNS,
  })
  return [
    columnHelper.accessor('user_name', {
      ...activityColumnHelper.retrieveColumn('user'),
      cell: (info) => {
        return (
          <div className="d-flex align-items-center gap-1">
            <MdPersonOutline size={20} />
            <div>{info.getValue()}</div>
          </div>
        )
      },
    }),
    columnHelper.display({
      ...activityColumnHelper.retrieveColumn('event'),
      size: 300,
      cell: ({ row }) => {
        return CellEventFormatterV2({ event: row.original })
      },
    }),
    columnHelper.accessor('bulk_size', {
      ...activityColumnHelper.retrieveColumn('bulk_size'),
      size: 100,
      cell: ({ getValue }) => {
        const value = getValue()
        return formatInteger(value)
      },
    }),
    columnHelper.accessor('content_type', {
      ...activityColumnHelper.retrieveColumn('content_type'),
      cell: ({ row }) => {
        return contentTypeMap[row.original.content_type.model]
      },
    }),
    columnHelper.display({
      ...activityColumnHelper.retrieveColumn('company'),
      size: 250,
      cell: ({ row }) => {
        if (!row.original.companies.length) return null

        if (row.original.bulk_size === 1) {
          return (
            <div className="break-words whitespace-normal max-w-full overflow-hidden">
              <FbLink
                to={`/company/${row.original.companies[0][0]}`}
                target="_blank"
                rel="noreferrer"
                className="inline-block max-w-full break-words whitespace-normal overflow-visible"
                style={{ textOverflow: 'clip' }}
              >
                {row.original.companies[0][1]}
              </FbLink>
            </div>
          )
        }

        // For reminders, bulk_size represents recipient count, not company count
        // So we shouldn't show ellipsis based on bulk_size for reminders
        const isReminder = row.original.content_type.model === 'reminder'
        const companiesNotShown = isReminder
          ? 0
          : row.original.bulk_size - row.original.companies.length
        const tooltipText =
          row.original.companies.map((company) => company[1]).join(', ') +
          (companiesNotShown > 0 ? `, ... (${companiesNotShown} more)` : '')

        return (
          <OverflownTextTooltip tooltipText={tooltipText} maxLines={1}>
            <div className="break-words whitespace-normal max-w-full overflow-hidden">
              {row.original.companies.map((company, index) => (
                <Fragment
                  key={'contact-companies-link-activity-table' + company[0]}
                >
                  <FbLink
                    to={`/company/${company[0]}`}
                    style={{
                      display: 'inline-block',
                      maxWidth: '100%',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'normal',
                      textOverflow: 'clip',
                      overflow: 'visible',
                    }}
                  >
                    {company[1]}
                    {index < row.original.companies.length - 1 && (
                      <span>,</span>
                    )}
                  </FbLink>
                  {index < row.original.companies.length - 1 && <span> </span>}
                </Fragment>
              ))}
              {companiesNotShown > 0 && ' ...'}
            </div>
          </OverflownTextTooltip>
        )
      },
    }),
    columnHelper.display({
      ...activityColumnHelper.retrieveColumn('door'),
      size: 250,
      cell: ({ row }) => {
        // Show door column for doorproxy, doornote, and reminder content types
        if (
          !['doorproxy', 'doornote', 'reminder'].includes(
            row.original.content_type.model
          ) ||
          !row.original.doors?.length
        ) {
          return null
        }

        if (row.original.bulk_size === 1) {
          return (
            <div className="break-words whitespace-normal max-w-full overflow-hidden">
              <FbLink
                to={`/door/UG${row.original.doors[0][0]}`}
                target="_blank"
                rel="noreferrer"
                className="inline-block max-w-full break-words whitespace-normal overflow-visible"
                style={{ textOverflow: 'clip' }}
              >
                {row.original.doors[0][1]}
              </FbLink>
            </div>
          )
        }

        const doorsNotShown =
          row.original.bulk_size - row.original.doors!.length
        const tooltipText =
          row.original.doors!.map((door) => door[1]).join(', ') +
          (doorsNotShown > 0 ? `, ... (${doorsNotShown} more)` : '')

        return (
          <OverflownTextTooltip tooltipText={tooltipText} maxLines={1}>
            <div className="break-words whitespace-normal max-w-full overflow-hidden">
              {row.original.doors!.map((door, index) => (
                <Fragment key={'doors-link-activity-table' + door[0]}>
                  <FbLink
                    to={`/door/UG${door[0]}`}
                    style={{
                      display: 'inline-block',
                      maxWidth: '100%',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'normal',
                      textOverflow: 'clip',
                      overflow: 'visible',
                    }}
                  >
                    {door[1]}
                    {index < row.original.doors!.length - 1 && <span>,</span>}
                  </FbLink>
                  {index < row.original.doors!.length - 1 && <span> </span>}
                </Fragment>
              ))}
              {doorsNotShown > 0 && ' ...'}
            </div>
          </OverflownTextTooltip>
        )
      },
    }),
    columnHelper.accessor('created_at', {
      ...activityColumnHelper.retrieveColumn('created_at'),
      size: 170,
      cell: ({ row }) => {
        const date = new Date(row.original.created_at)
        // Date needs to be formatted like this: May 16, 2023,  4:14 PM
        return (
          <span className="whitespace-nowrap">
            {date.toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: 'numeric',
              minute: 'numeric',
              hour12: true,
            })}
          </span>
        )
      },
    }),
  ]
}
